import { env } from '@/config/env';

/**
 * Приём нового пользователя.
 *
 * Вызывается сразу после анонимного входа: профиль и стартовые кредиты
 * заводит сервер, и до этого момента человек видел бы ноль генераций —
 * а мастер при нулевом балансе не даёт создать портрет. Клиент передаёт
 * только слабые сигналы (устройство, код приглашения); сколько выдать и
 * выдавать ли вообще, решает сервер.
 */
export interface OnboardingResult {
  /** Сколько кредитов начислено именно сейчас. 0 при повторном вызове. */
  granted: number;
  balance: number;
}

export class OnboardingError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'OnboardingError';
    this.code = code;
  }
}

interface ServerError {
  error?: { code?: string; message?: string };
}

export async function onboardUser(
  input: { deviceHash: string; referralCode?: string | undefined },
  accessToken: string,
): Promise<OnboardingResult> {
  let response: Response;

  try {
    response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/onboard`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        deviceHash: input.deviceHash,
        ...(input.referralCode ? { referralCode: input.referralCode } : {}),
      }),
    });
  } catch {
    throw new OnboardingError('Нет связи с сервером. Проверьте интернет.', 'NETWORK_ERROR');
  }

  const payload = (await response.json().catch(() => null)) as
    | (OnboardingResult & ServerError)
    | null;

  if (!payload) {
    throw new OnboardingError('Сервер ответил неожиданно.', 'UNKNOWN_ERROR');
  }

  if (!response.ok || payload.error) {
    throw new OnboardingError(
      payload.error?.message ?? 'Не удалось начать работу.',
      payload.error?.code ?? 'UNKNOWN_ERROR',
    );
  }

  return { granted: payload.granted, balance: payload.balance };
}
